import { PageController } from '@client/components/_core/layout/PageController';
import { type TabItem, Tabs } from '@client/components/_core/navigation/Tabs';
import { RecordTabLabel } from '@client/components/_ui/display/RecordPrimitives';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { confirmAction, confirmDestructive } from '@client/components/_ui/feedback/modal';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { RepairNetworkFormDrawer } from '@client/components/repair-networks/RepairNetworkFormDrawer';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useCustomDocumentTitle } from '@client/hooks/ui/useCustomDocumentTitle';
import { trpc } from '@client/trpc/client';
import { isRetryableQueryError } from '@client/trpc/error-classification';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { Building2, LayoutGrid, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NETWORKS_LIST_ROUTE, useCanManageRepairNetworks, useNetworkRecord } from './network-record-data';
import { RepairNetworkRecordHeader } from './RepairNetworkRecordHeader';

export const Route = createFileRoute('/console/$orgId/configuration/vendor-management/repair-networks/details/$id')({
    staticData: {
        presentation: 'fullPage',
        modules: ['claim'],
        metadata: {
            title: 'Repair network',
        },
        ui: {
            widthVariant: 'default',
            hideTitle: true,
        },
    },
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    beforeLoad(ctx): ShareableRouteContext {
        async function breadcrumb() {
            const network = await ctx.context.trpcUtils.repairNetwork.getRepairNetwork.ensureData({
                id: ctx.params.id,
            });
            return network.name;
        }

        return {
            breadcrumb: { title: breadcrumb },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <NetworkRecord />
        </PageController>
    );
}

function NetworkRecord() {
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const canEdit = useCanManageRepairNetworks();
    const [isEditOpen, setIsEditOpen] = useState(false);

    const goToList = (options?: { replace?: boolean }) =>
        navigate({ to: NETWORKS_LIST_ROUTE, params: { orgId }, ...options });

    const networkQuery = useNetworkRecord(id);
    const network = networkQuery.data;
    const isUnavailable = !!networkQuery.error && !isRetryableQueryError(networkQuery.error);

    // A missing or foreign-org id lands on the list with the server's message
    // rather than an empty record shell.
    useEffect(() => {
        if (isUnavailable) {
            errorMessage.show(networkQuery.error);
            goToList({ replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate only when the error changes
    }, [networkQuery.error, isUnavailable]);

    const { mutate: setDefault } = trpc.repairNetwork.setDefaultRepairNetwork.useMutation();
    const { mutate: deleteNetwork } = trpc.repairNetwork.deleteRepairNetwork.useMutation();

    useCustomDocumentTitle(network?.name);

    const invalidateLists = () => {
        void trpcUtils.repairNetwork.listRepairNetworks.invalidate();
        void trpcUtils.repairNetwork.listRepairNetworksForSelect.invalidate();
    };
    // Moving the default touches every other network record and every client that follows it.
    const invalidateEverythingButThisRecord = () => {
        invalidateLists();
        void trpcUtils.repairNetwork.getRepairNetwork.invalidate(undefined, {
            predicate: (query) => (query.queryKey[1] as { input?: { id?: bigint } } | undefined)?.input?.id !== id,
        });
        void trpcUtils.repairNetwork.listRepairNetworkClients.invalidate();
        void trpcUtils.repairNetwork.listSupplierRepairNetworks.invalidate();
        void trpcUtils.fleetClientDb.getFleetClient.invalidate();
        void trpcUtils.supplier.listSuppliers.invalidate();
    };

    const queryError = networkQuery.error ? (
        <InlineQueryError
            compact={!!network}
            title="Could not load repair network"
            description={
                isUnavailable
                    ? undefined
                    : network
                      ? 'The latest details could not be loaded. Previously loaded details have been kept.'
                      : 'The repair network could not be loaded. Please try again.'
            }
            error={networkQuery.error}
            onRetry={() => void networkQuery.refetch()}
            retrying={networkQuery.isFetching}
        />
    ) : null;

    if (!network || isUnavailable) {
        if (queryError) return queryError;
        return (
            <div className="rounded-lg border border-separator bg-card p-6">
                <Skeleton active />
            </div>
        );
    }

    const tabItems: TabItem[] = [
        {
            to: '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/overview',
            label: <RecordTabLabel icon={LayoutGrid}>Overview</RecordTabLabel>,
        },
        {
            to: '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/members',
            label: (
                <RecordTabLabel icon={Wrench} count={network.memberCount}>
                    Members
                </RecordTabLabel>
            ),
        },
        {
            // Fleet-module only: the Tabs wrapper hides it through the route's `modules`.
            to: '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/clients',
            label: (
                <RecordTabLabel icon={Building2} count={network.clientCount}>
                    Clients
                </RecordTabLabel>
            ),
        },
    ];

    const onSetDefault = () => {
        confirmAction({
            title: `Make ${network.name} the organisation default?`,
            content:
                'Every client without its own selection draws from the default network. The current default loses the tag; clients that chose it explicitly keep it.',
            okText: 'Set as default',
            onOk() {
                setDefault(
                    { id: network.id },
                    {
                        onSuccess() {
                            message.success(`${network.name} is now the organisation default.`);
                            invalidateEverythingButThisRecord();
                            void networkQuery.refetch();
                        },
                        onError: (error) => errorMessage.show(error),
                    },
                );
            },
        });
    };

    const onDelete = () => {
        confirmDestructive({
            title: `Delete ${network.name}?`,
            content: `No client uses this network. Its ${network.memberCount} ${network.memberCount === 1 ? 'membership is' : 'memberships are'} removed; the repairers themselves stay in vendor management and in any other network they belong to.`,
            okText: 'Delete network',
            onOk() {
                deleteNetwork(
                    { id: network.id },
                    {
                        onSuccess() {
                            message.success('Network deleted.');
                            // Invalidate the lists only: refetching the deleted record here
                            // could land a NOT_FOUND before unmount and show a second error.
                            invalidateLists();
                            void trpcUtils.repairNetwork.listSupplierRepairNetworks.invalidate();
                            void trpcUtils.supplier.listSuppliers.invalidate();
                            goToList();
                        },
                        onError: (error) => errorMessage.show(error),
                    },
                );
            },
        });
    };

    return (
        <div className="flex flex-col gap-4">
            {queryError}
            <RepairNetworkRecordHeader
                network={network}
                orgId={orgId}
                canEdit={canEdit}
                onEdit={() => setIsEditOpen(true)}
                onSetDefault={onSetDefault}
                onDelete={onDelete}
                backLink={
                    <BackLink to={NETWORKS_LIST_ROUTE} params={{ orgId }}>
                        Back to repair networks
                    </BackLink>
                }
            />

            <Tabs items={tabItems} />

            <RepairNetworkFormDrawer
                open={isEditOpen}
                network={network}
                onClose={() => setIsEditOpen(false)}
                onSaved={() => {
                    setIsEditOpen(false);
                    invalidateLists();
                    void networkQuery.refetch();
                }}
            />
        </div>
    );
}
